import type { NormalizedRect } from '@/domain/types'
import type { OcrReading } from '@/services/ocr-engine'

export type RankTitleState = 'pending' | 'target' | 'other' | 'no-titlebar' | 'unknown'
export type RankSourceRestriction = 'capture-not-window' | 'capture-title-mismatch'
export interface RankCaptureAssessment {
  readonly selectionId: number
  readonly displaySurface?: string
  readonly title: RankTitleState
}

export function rankSourceRestriction(value: RankCaptureAssessment): RankSourceRestriction | null {
  if (value.displaySurface !== 'window') return 'capture-not-window'
  return value.title === 'other' ? 'capture-title-mismatch' : null
}

interface Component { left: number; right: number; top: number; bottom: number; points: [number, number][] }

/** Conservative Windows caption detection. Unrecognized decorations are never evidence of another server. */
export function locateRankTitlebar(frame: ImageData): NormalizedRect | null {
  const { width: w, height: fh, data } = frame
  if (w < 300 || fh < 200) return null
  const color = (x: number, y: number) => {
    const i = (y * w + x) * 4
    return [data[i]!, data[i + 1]!, data[i + 2]!] as const
  }
  const distance = (x: number, y: number, bg: readonly number[]) => Math.max(...color(x, y).map((c, i) => Math.abs(c - bg[i]!)))
  const rowFraction = (y: number, bg: readonly number[], tolerance = 24) => {
    let same = 0, total = 0
    for (let x = Math.round(w * .2); x < w * .82; x += Math.max(1, Math.floor(w / 300))) {
      total++; if (distance(x, y, bg) < tolerance) same++
    }
    return same / total
  }
  for (let h = 14; h <= Math.min(140, Math.floor(fh * .18)); h++) {
    const bg = color(Math.floor(w * .55), Math.floor(h * .45))
    if (rowFraction(h - 2, bg) < .90 || rowFraction(h + 1, bg, 8) > .65
      || [.25, .45, .65].some(f => rowFraction(Math.floor(h * f), bg) < .85)) continue
    const left = Math.max(0, w - Math.ceil(h * 5)), top = Math.floor(h * .16), bottom = Math.ceil(h * .82)
    const seen = new Set<number>(), components: Component[] = []
    for (let y = top; y < bottom; y++) for (let x = left; x < w - 2; x++) {
      const key = y * w + x
      if (seen.has(key) || distance(x, y, bg) < 12) continue
      const stack: [number, number][] = [[x, y]], points: [number, number][] = []
      let l = x, r = x, t = y, b = y
      seen.add(key)
      while (stack.length) {
        const [xx, yy] = stack.pop()!
        points.push([xx, yy]); l = Math.min(l, xx); r = Math.max(r, xx); t = Math.min(t, yy); b = Math.max(b, yy)
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const nx = xx + dx, ny = yy + dy, nk = ny * w + nx
          if (nx < left || nx >= w - 2 || ny < top || ny >= bottom || seen.has(nk) || distance(nx, ny, bg) < 12) continue
          seen.add(nk); stack.push([nx, ny])
        }
      }
      if (r - l >= h * .15 && r - l <= h * .65 && b - t < h * .65) components.push({ left: l, right: r, top: t, bottom: b, points })
    }
    const close = components.find(c => {
      const cw = c.right - c.left, ch = c.bottom - c.top
      if (c.left < w - h * 1.6 || ch < h * .16 || cw / ch < .7 || cw / ch > 1.4) return false
      return c.points.filter(([x, y]) => Math.min(Math.abs((x - c.left) / cw - (y - c.top) / ch),
        Math.abs((x - c.left) / cw + (y - c.top) / ch - 1)) < .22).length / c.points.length > .8
    })
    if (!close) continue
    const minimize = components.find(c => c.right < close.left - h * 1.7 && c.right > close.left - h * 4.5
      && c.bottom - c.top < h * .12)
    const maximize = components.find(c => c.left > (minimize?.right ?? w) && c.right < close.left - h * .5
      && c.bottom - c.top > h * .15)
    if (!minimize || !maximize) continue
    const x = Math.ceil(h * .8), right = minimize.left - Math.ceil(h * .7)
    if (right <= x) continue
    return { x: x / w, y: 2 / fh, width: (right - x) / w, height: (h - 4) / fh }
  }
  return null
}

export function classifyRankTitle(reading: OcrReading | null, width: number, height: number): RankTitleState {
  if (!reading || !Number.isFinite(reading.confidence) || reading.confidence < .9 || reading.items.length !== 1
    || reading.detectedItems !== reading.acceptedItems) return 'unknown'
  const item = reading.items[0]!
  if (!Number.isFinite(item.confidence) || item.confidence < .9 || item.polygon.length < 4 || item.polygon.some(([x, y]) =>
    !Number.isFinite(x) || !Number.isFinite(y) || x <= 1 || y <= 1 || x >= width - 1 || y >= height - 1)) return 'unknown'
  const title = item.text.normalize('NFKC').replace(/\s+/gu, '')
  if (!title || /(?:\.{3}|…)$/.test(title)) return 'unknown'
  return title === '冒险岛怀旧服' ? 'target' : 'other'
}
